import { quickCashflowAssistApi } from 'api/quickCashflowAssistApi';
import { persistAssistExtractToFinancialSnapshotApi } from 'api/financialSnapshotExtractApi';
import {
  cancelQuickCashflowExtractJob,
  getQuickCashflowExtractStatus,
} from 'api/quickCashflowModelApi';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  applyAssistResponse,
  buildAssistChatText,
} from './applyAssistResponse';
import { buildMenuActionsFromSession } from './buildMenuActions';
import {
  applyExtractPollTerminal,
  isExtractJobComplete,
  resolveExtractPollPayload,
} from './resolveExtractPollPayload';
import { createDefaultAssistSession } from './constants';

const QCF_WELCOME_MESSAGES = [
  {
    role: 'ai',
    text: 'Welcome! Choose **Cohort**, **Accounting**, or **Marketing** to upload, use Google Drive, or describe in chat.',
  },
];

function extractHasPersistableData(response) {
  if (!response) {
    return false;
  }
  return Boolean(
    response.accountingSnapshot ||
      response.cohortSheet?.cohorts?.length ||
      response.cohort?.applied
  );
}

export function useQuickCashflowAssist({
  customerId,
  customerName,
  toast,
  onExtractPersisted,
}) {
  const [assistSession, setAssistSession] = useState(createDefaultAssistSession);
  const [cohortSheet, setCohortSheet] = useState(null);
  const [accountingSnapshot, setAccountingSnapshot] = useState(null);
  const [droppedFiles, setDroppedFiles] = useState([]);
  const [extractChatAppend, setExtractChatAppend] = useState(null);
  const [extractUsage, setExtractUsage] = useState(null);
  const [cancellingExtract, setCancellingExtract] = useState(false);
  const [assistChatKey, setAssistChatKey] = useState(0);

  const assistSessionRef = useRef(createDefaultAssistSession());
  const cohortSheetRef = useRef(null);
  const extractPollRef = useRef(null);

  useEffect(() => {
    return () => {
      if (extractPollRef.current) {
        clearInterval(extractPollRef.current);
      }
    };
  }, []);

  const resetAssist = useCallback(() => {
    setAssistSession(createDefaultAssistSession());
    setCohortSheet(null);
    setAccountingSnapshot(null);
    setExtractUsage(null);
    setExtractChatAppend(null);
    assistSessionRef.current = createDefaultAssistSession();
    cohortSheetRef.current = null;
    setAssistChatKey((k) => k + 1);
  }, []);

  const persistExtract = useCallback(
    async (response, triggeredBy) => {
      if (!customerId || !extractHasPersistableData(response)) {
        return null;
      }
      try {
        const updated = await persistAssistExtractToFinancialSnapshotApi(
          customerId,
          response,
          triggeredBy || `${customerName || 'Customer'} — Data Extractor assist`
        );
        onExtractPersisted?.(updated);
        return updated;
      } catch (err) {
        toast?.({
          title: 'Could not save extract to Data snapshot',
          description: err?.response?.data?.error || err.message,
          status: 'error',
        });
        return null;
      }
    },
    [customerId, customerName, onExtractPersisted, toast]
  );

  const applyLocalExtract = useCallback((response, fileNames) => {
    const base = {
      assistSession: assistSessionRef.current,
      cohortSheet: cohortSheetRef.current,
      accountingSnapshot,
    };
    const next = applyAssistResponse(base, response, fileNames);
    if (next.assistSession) {
      assistSessionRef.current = next.assistSession;
      setAssistSession(next.assistSession);
    }
    if (next.cohortSheet) {
      cohortSheetRef.current = next.cohortSheet;
      setCohortSheet(next.cohortSheet);
    }
    if (next.accountingSnapshot) {
      setAccountingSnapshot(next.accountingSnapshot);
    }
    return next;
  }, [accountingSnapshot]);

  const stopExtractPoll = useCallback(() => {
    if (extractPollRef.current) {
      clearInterval(extractPollRef.current);
      extractPollRef.current = null;
    }
  }, []);

  const applyTerminalExtract = useCallback(
    (qcf, extractJob) => {
      if (qcf) {
        const next = applyLocalExtract(qcf, []);
        const terminal = applyExtractPollTerminal(next, qcf, extractJob);
        if (terminal.assistSession) {
          assistSessionRef.current = terminal.assistSession;
          setAssistSession(terminal.assistSession);
        }
        if (terminal.accountingSnapshot) {
          setAccountingSnapshot(terminal.accountingSnapshot);
        }
        return;
      }
      if (!extractJob) {
        return;
      }
      setAssistSession((s) => ({
        ...s,
        extractJob,
      }));
      assistSessionRef.current = {
        ...assistSessionRef.current,
        extractJob,
      };
    },
    [applyLocalExtract]
  );

  const pollExtractJob = useCallback(
    (jobId) => {
      stopExtractPoll();
      const started = Date.now();
      extractPollRef.current = setInterval(async () => {
        if (Date.now() - started > 15 * 60 * 1000) {
          stopExtractPoll();
          return;
        }
        try {
          const status = await getQuickCashflowExtractStatus('data-page', jobId);
          const { extractJob, qcf } = resolveExtractPollPayload(status);
          if (isExtractJobComplete(extractJob?.status) && qcf) {
            stopExtractPoll();
            applyTerminalExtract(qcf, extractJob);
            await persistExtract(qcf, 'Data Extractor assist');
          } else if (extractJob?.status === 'cancelled') {
            stopExtractPoll();
            applyTerminalExtract(qcf, extractJob);
          } else if (extractJob?.status === 'failed') {
            stopExtractPoll();
            applyTerminalExtract(qcf, extractJob);
            toast?.({
              title: 'Extraction failed',
              description: extractJob.error || qcf?.explanation || 'Unknown error',
              status: 'error',
            });
          } else if (extractJob?.status === 'running' || extractJob?.status === 'pending') {
            setAssistSession((s) => ({
              ...s,
              extractJob: extractJob || s.extractJob,
            }));
            assistSessionRef.current = {
              ...assistSessionRef.current,
              extractJob: extractJob || assistSessionRef.current?.extractJob,
            };
          }
        } catch (err) {
          const msg = String(err?.response?.data?.error || '');
          if (msg.toLowerCase().includes('not found')) {
            stopExtractPoll();
            applyTerminalExtract(null, {
              ...(assistSessionRef.current?.extractJob || {}),
              jobId,
              status: 'failed',
              error: 'Extraction job was lost. Run the extract again.',
            });
            toast?.({
              title: 'Extraction stopped',
              description: 'The extract job is no longer running. Run it again.',
              status: 'error',
            });
          }
        }
      }, 3000);
    },
    [applyTerminalExtract, persistExtract, stopExtractPoll, toast]
  );

  const handleAssistSend = useCallback(
    async ({ message, files, history, cohortColumnMapping, signal }) => {
      const response = await quickCashflowAssistApi({
        message,
        files,
        history,
        cohortColumnMapping,
        assistSession: {
          ...(assistSessionRef.current ?? createDefaultAssistSession()),
          customerId,
        },
        cohortSheet: cohortSheetRef.current,
        customerId,
        signal,
      });
      applyLocalExtract(response, files.map((f) => f.name));
      if (response?.extractJob?.jobId && response.extractJob.status === 'running') {
        setExtractUsage(null);
        setExtractChatAppend(null);
        pollExtractJob(response.extractJob.jobId);
      } else {
        await persistExtract(response, 'Data Extractor assist');
      }
      return {
        text: buildAssistChatText(response),
        usage: response.usage,
        awaitingMappingConfirmation:
          response?.cohort?.clarificationKind === 'mapping_confirmation',
        cohortColumnMapping: response.cohort?.columnMapping,
      };
    },
    [applyLocalExtract, customerId, persistExtract, pollExtractJob]
  );

  const handleCancelExtract = useCallback(async () => {
    const jobId = assistSessionRef.current?.extractJob?.jobId;
    if (!jobId) {
      return;
    }
    setCancellingExtract(true);
    try {
      const status = await cancelQuickCashflowExtractJob(jobId);
      stopExtractPoll();
      const { extractJob, qcf } = resolveExtractPollPayload(status);
      applyTerminalExtract(qcf, extractJob);
      if (qcf) {
        const triggeredBy = isExtractJobComplete(extractJob?.status)
          ? 'Data Extractor assist'
          : 'Data Extractor assist (cancelled)';
        await persistExtract(qcf, triggeredBy);
      }
    } catch (err) {
      stopExtractPoll();
      try {
        const status = await getQuickCashflowExtractStatus('data-page', jobId);
        const { extractJob, qcf } = resolveExtractPollPayload(status);
        applyTerminalExtract(qcf, extractJob);
        if (isExtractJobComplete(extractJob?.status) && qcf) {
          await persistExtract(qcf, 'Data Extractor assist');
        }
      } catch {
        applyTerminalExtract(null, {
          ...(assistSessionRef.current?.extractJob || {}),
          jobId,
          status: 'cancelled',
        });
        toast?.({
          title: 'Could not cancel extraction',
          description: err?.response?.data?.error || err.message,
          status: 'error',
        });
      }
    } finally {
      setCancellingExtract(false);
    }
  }, [applyTerminalExtract, persistExtract, stopExtractPoll, toast]);

  const assistMenu = useMemo(
    () => buildMenuActionsFromSession(assistSession),
    [assistSession]
  );

  return {
    assistChatKey,
    assistMenu,
    assistSession,
    accountingSnapshot,
    cohortSheet,
    droppedFiles,
    setDroppedFiles,
    extractChatAppend,
    extractUsage,
    cancellingExtract,
    handleAssistSend,
    handleCancelExtract,
    resetAssist,
    welcomeMessages: QCF_WELCOME_MESSAGES,
  };
}
